import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { FiArrowRight, FiArrowLeft, FiAlertCircle, FiRefreshCw, FiChevronRight } from 'react-icons/fi';
import CategoryCard from '../common/CategoryCard';
import categoryService, { buildCategoryHierarchy } from '../../services/categoryService';
import { useRealtimeSync } from '../../hooks/useRealtimeSync';
import './CategoriesSection.css';

const CategoriesSection = () => {
    const [categories, setCategories] = useState([]);
    const [activeParentId, setActiveParentId] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const isMountedRef = useRef(true);
    const lastFetchedAtRef = useRef(0);

    useEffect(() => {
        isMountedRef.current = true;
        return () => {
            isMountedRef.current = false;
        };
    }, []);

    const fetchCategories = useCallback(async ({ silent = false, forceRefresh = false } = {}) => {
        try {
            if (!silent && isMountedRef.current) {
                setLoading(true);
                setError('');
            }
            const list = await categoryService.getAll({}, { forceRefresh });
            if (!isMountedRef.current) return;
            setCategories(Array.isArray(list) ? list : []);
            setError('');
            lastFetchedAtRef.current = Date.now();
        } catch (err) {
            if (!isMountedRef.current) return;
            if (!silent) {
                setError(err?.message || 'Failed to load categories. Please try again.');
            }
        } finally {
            if (!silent && isMountedRef.current) {
                setLoading(false);
            }
        }
    }, []);

    useEffect(() => {
        fetchCategories();
    }, [fetchCategories]);

    // Real-time synchronization: immediately refresh category images & metadata on admin mutations
    useRealtimeSync('categories', useCallback(() => {
        fetchCategories({ silent: true, forceRefresh: true });
    }, [fetchCategories]));

    // Refresh silently when returning to browser tab (e.g. after updating images in Admin Dashboard)
    useEffect(() => {
        const handleVisibilityChange = () => {
            if (
                document.visibilityState === 'visible' &&
                Date.now() - lastFetchedAtRef.current > 3000
            ) {
                fetchCategories({ silent: true, forceRefresh: true });
            }
        };
        document.addEventListener('visibilitychange', handleVisibilityChange);
        return () => {
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
    }, [fetchCategories]);

    const hierarchy = useMemo(() => buildCategoryHierarchy(categories), [categories]);

    // Reset drill-down if the selected parent was removed or no longer has subcategories
    useEffect(() => {
        if (
            activeParentId &&
            categories.length > 0 &&
            (!hierarchy.getCategoryById(activeParentId) || !hierarchy.hasSubcategories(activeParentId))
        ) {
            setActiveParentId(null);
        }
    }, [activeParentId, categories.length, hierarchy]);

    const activeParent = activeParentId ? hierarchy.getCategoryById(activeParentId) : null;
    const ancestors = useMemo(
        () => (activeParent ? hierarchy.getAncestors(activeParent._id) : []),
        [activeParent, hierarchy]
    );

    const displayedCategories = activeParent
        ? hierarchy.getSubcategories(activeParent._id)
        : hierarchy.parentCategories;

    const handleBackClick = () => {
        if (activeParent?.parent_id) {
            setActiveParentId(String(activeParent.parent_id));
        } else {
            setActiveParentId(null);
        }
    };

    const parentBackLabel = activeParent?.parent_id
        ? hierarchy.getCategoryById(activeParent.parent_id)?.name || 'Main Categories'
        : 'Main Categories';

    return (
        <section id="categories-section" className="categories-section" aria-label="Shop by appliance category">
            <div className="container">
                <div className="categories-header">
                    <span className="section-eyebrow">
                        {activeParent ? 'EXPLORE SUBCATEGORIES' : 'BROWSE CATALOG'}
                    </span>
                    <h2 className="section-title">
                        {activeParent ? activeParent.name : 'Shop by Category'}
                    </h2>
                    <p className="categories-subtitle">
                        {activeParent
                            ? `Select a subcategory under ${activeParent.name} to browse available models, cash discounts, and installment plans`
                            : 'Genuine factory-sealed home appliances with authorized manufacturer warranties, instant cash discounts, and easy installment plans'}
                    </p>
                </div>

                {loading ? (
                    <div className="categories-grid-wrap" aria-label="Loading categories">
                        {[1, 2, 3, 4].map((n) => (
                            <div key={n} className="category-card-skeleton" />
                        ))}
                    </div>
                ) : error ? (
                    <div className="categories-error-state">
                        <FiAlertCircle className="categories-state-icon" />
                        <p>{error}</p>
                        <button
                            type="button"
                            className="categories-retry-btn"
                            onClick={() => fetchCategories({ silent: false, forceRefresh: true })}
                        >
                            <FiRefreshCw /> Retry
                        </button>
                    </div>
                ) : displayedCategories.length === 0 ? (
                    <div className="categories-empty-state">
                        <FiAlertCircle className="categories-state-icon" />
                        <p>No categories currently available.</p>
                        {activeParent && (
                            <button
                                type="button"
                                className="categories-retry-btn"
                                onClick={() => setActiveParentId(null)}
                            >
                                <FiArrowLeft /> Back to Main Categories
                            </button>
                        )}
                    </div>
                ) : (
                    <>
                        {activeParent && (
                            <div className="categories-hierarchy-bar">
                                <button
                                    type="button"
                                    className="categories-back-btn"
                                    onClick={handleBackClick}
                                >
                                    <FiArrowLeft aria-hidden="true" />
                                    <span>Back to {parentBackLabel}</span>
                                </button>

                                <nav className="categories-breadcrumb" aria-label="Category hierarchy">
                                    <button
                                        type="button"
                                        className="categories-crumb-btn"
                                        onClick={() => setActiveParentId(null)}
                                    >
                                        Main Categories
                                    </button>
                                    {ancestors.map((anc) => (
                                        <React.Fragment key={anc._id}>
                                            <FiChevronRight className="categories-crumb-sep" aria-hidden="true" />
                                            <button
                                                type="button"
                                                className="categories-crumb-btn"
                                                onClick={() => setActiveParentId(String(anc._id))}
                                            >
                                                {anc.name}
                                            </button>
                                        </React.Fragment>
                                    ))}
                                    <FiChevronRight className="categories-crumb-sep" aria-hidden="true" />
                                    <span className="categories-crumb-current">{activeParent.name}</span>
                                </nav>
                            </div>
                        )}

                        {/* ── Dynamic Parent / Subcategory Grid ── */}
                        <div className="categories-grid-wrap">
                            {displayedCategories.map((category) => (
                                <CategoryCard
                                    key={category._id}
                                    category={category}
                                    subcategoryCount={hierarchy.getSubcategories(category._id).length}
                                    onSelectParent={(selected) => setActiveParentId(String(selected._id))}
                                />
                            ))}
                        </div>

                        {/* Centered CTA Button to View Full Catalog */}
                        <div className="categories-cta-wrap">
                            <Link
                                to={
                                    activeParent
                                        ? `/products?parent=${encodeURIComponent(activeParent._id)}`
                                        : '/products'
                                }
                                className="categories-view-all-btn"
                            >
                                <span>
                                    {activeParent ? 'Browse Full Product Catalog' : 'View All Categories'}
                                </span>
                                <FiArrowRight aria-hidden="true" />
                            </Link>
                        </div>
                    </>
                )}
            </div>
        </section>
    );
};

export default CategoriesSection;

